import { useCallback, useEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { config } from '../../config';
import { log } from '../../app/logger';
import { classifyPointerEnd, movedBeyondSlop } from '../../domain/gesture';

/**
 * DD-1 (design §5.5) — the **gesture layer**: Pointer Events, timers and target resolution.
 *
 * Build unit U-8 owns this; §16 makes U-9's locked board depend on it, so it lives in `ui/capture/`
 * beside the §5.6 chrome rather than inside `ui/discovery/`. The *rules* are in
 * `domain/gesture.ts`, where a Node suite drives every threshold; what is here is the part a Node
 * suite cannot hold — real `PointerEvent`s, a 450 ms timer, and the DOM read that resolves which
 * hypothesis the finger landed on.
 *
 * ── `pointerdown` resolves the target, and that is the DISC-7 correctness layer ──
 *
 * §5.1 steps 1–2: *"Resolve `hypothesisId` from the touched element's `data-hid` **at pointerdown**.
 * This is the load-bearing correctness mechanism for DISC-7 and LIVE-11: no reorder at any time —
 * mid-animation included — can retarget a tap that has already begun. Resolve `data-role`
 * (`tile` | `undo`) — the undo inset is a distinct hit region."*
 *
 * The resolution walks **up** from `event.target` with `closest()`, not from `currentTarget`, and
 * that is the whole point of the two attributes being separate: the `−1` inset (LIVE-6, build unit
 * U-10) sits *inside* the row that carries `data-hid`, so `closest('[data-role]')` finds the inset
 * while `closest('[data-hid]')` finds the row. One tap cannot be both.
 *
 * ── Never `click` ────────────────────────────────────────────────────────────
 *
 * DD-1: *"Pointer Events throughout; never `click`."* `click` on iOS carries the double-tap delay
 * this surface cannot afford, fires after a scroll gesture the user meant as a pan, and gives no
 * pointerdown moment at which to pre-build the row. Nothing here listens for it.
 *
 * ── The payload, and why it is generic ───────────────────────────────────────
 *
 * `onPressStart` returns a value the hook holds until the contact ends, then hands to `onTap`. That
 * is §5.1 step 4's pre-built `Increment` command travelling from pointerdown to pointerup **as a
 * value**, so the dispatch cannot pick up a target that changed in between. It is keyed by
 * `pointerId` rather than stored in one slot, because two fingers on two rows are two contacts and a
 * single slot would let the second overwrite the first's target.
 */

/**
 * §5.1 step 2's hit regions, as values rather than as string literals at four call sites.
 *
 * Both surfaces render the `−1` inset **inside** the element carrying `data-hid`, so
 * `closest('[data-role]')` finds the inset while `closest('[data-hid]')` still finds the row. One
 * contact resolves to exactly one region, at `pointerdown`, before anything can move (DISC-7).
 */
export const HIT_REGION = {
  /** The row or tile body. A tap here increments (LIVE-2, DISC-3). */
  tile: 'tile',
  /** LIVE-6's `−1` inset. A tap here undoes and **never** increments. */
  undo: 'undo',
} as const;

/** What `pointerdown` resolved out of the DOM. */
export interface GestureTarget {
  /** `data-hid` — the hypothesis. */
  readonly hid: string;
  /** `data-role` — §5.1 step 2's hit region. See {@link HIT_REGION}. */
  readonly role: string;
  /** The element carrying `data-hid`, held so §5.1 step 9 can toggle the ack class on the ref. */
  readonly element: HTMLElement;
}

/**
 * What a contact on a capture surface turns out to mean — resolved at `pointerdown` from the hit
 * region, and carried to `pointerup` **as a value**.
 *
 * §6.4's negative is the reason this is a discriminated union rather than a nullable command: *"a tap
 * in the tile's non-inset region during an open undo window MUST increment, not undo."* Both
 * directions of that sentence are decided once, at the moment the finger lands, from the element it
 * landed on — so no reflow, animation or settle between `pointerdown` and `pointerup` can turn one
 * into the other.
 */
export type CaptureIntent<Command> =
  | { readonly kind: 'increment'; readonly command: Command }
  | { readonly kind: 'undo' };

export interface PointerGestureSpec<P> {
  /**
   * Fires at pointerdown, before any classification. Returns the value handed to {@link onTap}.
   *
   * Return `null` to decline the contact entirely — the row is not capturing (see
   * `app/discovery.canCapture`), so no timer is armed and no later callback fires for it.
   */
  readonly onPressStart: (target: GestureTarget, at: number) => P | null;
  /** A completed tap: `|Δ| < TAP_SLOP_PX ∧ Δt < LONG_PRESS_MS`, and no hold fired. */
  readonly onTap: (target: GestureTarget, payload: P, at: number) => void;
  /** The 450 ms hold, fired **while the finger is still down**. AUD-2's recorder is U-11's. */
  readonly onLongPress?: (target: GestureTarget, at: number) => void;
  /** LIVE-5's swipe-left. Locked board only (U-9) — Discovery leaves `swipeLeft` false. */
  readonly onSwipeLeft?: (target: GestureTarget, at: number) => void;
  /** Every contact ends here, whatever it was classified as — including `pointercancel`. */
  readonly onPressEnd?: (target: GestureTarget) => void;
  /** DISC-7's *"and no pointer is down"* half of the quiet gate. */
  readonly onActiveChange?: (pointerDown: boolean) => void;
  /** DD-1 — `false` everywhere except the locked board. */
  readonly swipeLeft?: boolean;
  /** Injected so tests and the browser pass share one clock with the reorder gate. */
  readonly now?: () => number;
}

interface Contact<P> {
  readonly target: GestureTarget;
  readonly payload: P;
  readonly startedAt: number;
  readonly x: number;
  readonly y: number;
  longPressFired: boolean;
  timer: number | undefined;
}

/** The four handlers to spread onto a row or tile. There is deliberately no `onClick`. */
export interface PointerGestureHandlers {
  readonly onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

/**
 * Explicit capture so `pointerup` returns to this element even if the finger drifts off it.
 *
 * Touch pointers are implicitly captured already; this makes a mouse behave the same way, so desktop
 * verification exercises the same path a thumb does. When the browser decides the gesture is a pan
 * it fires `pointercancel` and releases the capture itself — which is exactly the arbitration DD-1
 * wants, since Discovery's horizontal axis belongs to the pager.
 *
 * **It is allowed to fail, and failing must not cost an observation.** `setPointerCapture` throws
 * `NotFoundError` when the pointer is no longer active — a browser that released or cancelled the
 * contact between queueing the event and running this handler. An uncaught throw here would abandon
 * the contact before the row was even armed, so the tap would vanish with no error the user could
 * see. The browser pass produced exactly that (from an invalid synthetic `pointerId`), and it is the
 * loudest possible argument for not letting a convenience call sit on the capture path.
 *
 * Handled and reported, never swallowed (NFR-8): the contact proceeds on implicit capture, which is
 * what the target runtime uses anyway.
 */
function capture(element: HTMLElement, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch (cause) {
    log.warn('ui/gesture', 'pointer capture was refused; continuing on implicit capture', {
      pointerId,
      message: cause instanceof Error ? cause.message : String(cause),
    });
  }
}

function resolveTarget(event: ReactPointerEvent<HTMLElement>): GestureTarget | null {
  const from = event.target instanceof Element ? event.target : event.currentTarget;
  const element = from.closest<HTMLElement>('[data-hid]');
  if (element === null) return null;
  const hid = element.dataset.hid;
  if (hid === undefined || hid === '') return null;
  const roleHost = from.closest<HTMLElement>('[data-role]');
  return { hid, role: roleHost?.dataset.role ?? '', element };
}

export function usePointerGesture<P>(spec: PointerGestureSpec<P>): PointerGestureHandlers {
  const contacts = useRef(new Map<number, Contact<P>>());
  const latest = useRef(spec);
  latest.current = spec;

  const clock = useCallback((): number => (latest.current.now ?? Date.now)(), []);

  const finish = useCallback((pointerId: number): Contact<P> | undefined => {
    const contact = contacts.current.get(pointerId);
    if (contact === undefined) return undefined;
    if (contact.timer !== undefined) window.clearTimeout(contact.timer);
    contacts.current.delete(pointerId);
    latest.current.onPressEnd?.(contact.target);
    latest.current.onActiveChange?.(contacts.current.size > 0);
    return contact;
  }, []);

  // A contact left armed when the surface unmounts would fire its hold against a torn-down tree.
  // The map is the only thing holding the timers, so clearing it here is the whole cleanup.
  useEffect(() => {
    const live = contacts.current;
    return () => {
      for (const contact of live.values()) {
        if (contact.timer !== undefined) window.clearTimeout(contact.timer);
      }
      live.clear();
    };
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>): void => {
      const target = resolveTarget(event);
      if (target === null) return;
      const at = clock();
      const payload = latest.current.onPressStart(target, at);
      if (payload === null) return;

      capture(event.currentTarget, event.pointerId);

      const contact: Contact<P> = {
        target,
        payload,
        startedAt: at,
        x: event.clientX,
        y: event.clientY,
        longPressFired: false,
        timer: undefined,
      };
      contact.timer = window.setTimeout(() => {
        contact.timer = undefined;
        contact.longPressFired = true;
        latest.current.onLongPress?.(target, clock());
      }, config.LONG_PRESS_MS);

      contacts.current.set(event.pointerId, contact);
      latest.current.onActiveChange?.(true);
    },
    [clock],
  );

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>): void => {
    const contact = contacts.current.get(event.pointerId);
    if (contact === undefined || contact.timer === undefined) return;
    const travel = {
      dx: event.clientX - contact.x,
      dy: event.clientY - contact.y,
      elapsedMs: 0,
    };
    // §5.5 — "cancelled by > 10 px movement". Only the hold is cancelled here; the contact stays
    // alive, because a finger that wandered and came back inside the slop is still a tap.
    if (movedBeyondSlop(travel)) {
      window.clearTimeout(contact.timer);
      contact.timer = undefined;
    }
  }, []);

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLElement>): void => {
      const contact = finish(event.pointerId);
      if (contact === undefined) return;
      const at = clock();
      const kind = classifyPointerEnd(
        {
          dx: event.clientX - contact.x,
          dy: event.clientY - contact.y,
          elapsedMs: at - contact.startedAt,
        },
        { swipeLeft: latest.current.swipeLeft ?? false, longPressFired: contact.longPressFired },
      );

      if (kind === 'tap') latest.current.onTap(contact.target, contact.payload, at);
      else if (kind === 'swipeLeft') latest.current.onSwipeLeft?.(contact.target, at);
      // 'longPress' never arrives here — the hold already fired from its timer — and 'none' is a
      // real answer: §5.5's "discard, no mutation".
    },
    [clock, finish],
  );

  const onPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLElement>): void => {
      // §5.5: "`pointercancel` → discard, no mutation." The browser took the gesture for a pan or a
      // system UI, and nothing about it is a capture. It is not classified at all.
      finish(event.pointerId);
    },
    [finish],
  );

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
